import type { EmailBodyDto } from '@carbonwebmail/contracts'
import { proseBody } from '@carbonwebmail/domain'
import { useRef } from 'react'
import { Link } from 'react-router'
import { TextAreaField } from './Field.js'
import { BODY_HINT, MergeTokens } from './MergeTokens.js'

/**
 * Writing the message, in whichever of the two ways it was written.
 *
 * **Prose and a design are both first-class, and the body says which it is.**
 * Somebody who wants to write four sentences and send them should not have to
 * open a layout editor, and somebody laying out a newsletter should not be
 * typing into a textarea. Guessing from the markup was the alternative and it
 * is wrong the first time a person types a `<`.
 *
 * **A design is not edited here.** Laying one out needs the whole width, so it
 * has a screen of its own and this shows what there is with a way into it. A
 * block builder squeezed into a form column beside a preview is one nobody can
 * use — which is what it was, and why it did not read as a compose feature at
 * all.
 *
 * Shared by the campaign editor and the template editor, which want the
 * identical thing. One copy, so a fix to either mode reaches both.
 */

export function BodyEditor({
  id,
  body,
  disabled,
  composeAt,
  onChange,
}: {
  /** Distinct per screen, because two editors on one page would collide. */
  readonly id: string
  readonly body: EmailBodyDto
  readonly disabled: boolean
  /** Where Compose lives for whatever this message belongs to. */
  readonly composeAt: string
  readonly onChange: (body: EmailBodyDto) => void
}) {
  const field = useRef<HTMLTextAreaElement>(null)

  if (body.kind === 'designed') {
    return (
      <section>
        <p className="mb-2 font-semibold text-[13px] text-t1">Message</p>
        {/*
          The design itself, at a readable size. Somebody opening this screen has
          to recognise what they are about to edit, and a line of text saying "a
          design" tells them nothing about which one.
        */}
        <div className="h-[320px] overflow-hidden rounded-card border border-line bg-white">
          <iframe
            title="The message as it stands"
            srcDoc={body.html}
            sandbox=""
            scrolling="no"
            className="h-[1000px] w-[700px] origin-top-left scale-[0.62] border-0"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Link
            className="rounded-pill bg-brand px-4 py-2 font-semibold text-[13px] text-brand-contrast"
            to={composeAt}
          >
            Open in Compose
          </Link>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (window.confirm(BACK_TO_PROSE)) onChange(proseBody(''))
            }}
            className="font-medium text-[12px] text-t3 hover:text-t1 disabled:opacity-60"
          >
            Write as plain text instead
          </button>
        </div>
      </section>
    )
  }

  return (
    <>
      <TextAreaField
        id={id}
        label="Message"
        rows={18}
        value={body.text}
        disabled={disabled}
        fieldRef={field}
        hint={BODY_HINT}
        onChange={(value) => onChange(proseBody(value))}
      />
      <MergeTokens
        field={field}
        value={body.text}
        disabled={disabled}
        onChange={(value) => onChange(proseBody(value))}
      />

      <div className="mt-4 flex flex-wrap items-center gap-3 border-line border-t pt-4">
        {/*
          A link rather than a button that swaps an editor in. Compose is a
          screen, and what is typed here is carried into it from the server —
          so this promises nothing it cannot keep about unsaved words.
        */}
        <Link
          className="rounded-pill border border-brand px-3.5 py-1.5 font-semibold text-[12px] text-brand hover:bg-brand-soft"
          to={composeAt}
        >
          Design it with blocks
        </Link>
        <p className="text-[12px] text-t3">
          Images, buttons, columns and colours, on a screen of its own. Save this first — Compose
          opens what is stored.
        </p>
      </div>
    </>
  )
}

/** Losing a layout is not something to do on a mis-click. */
const BACK_TO_PROSE =
  'Going back to plain text discards the layout — the blocks, images and colours are not kept.\n\nWrite this message as plain text instead?'
